import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { parseUrlForProjectId, parseUrlForProgrammeId } from "../../../constants";
import { useGetProgrammeByProjectId } from "../../../contexts/permissions/PermissionHooks";
import PermissionsContext from "../../../contexts/permissions/PermissionsContext";
import { PermissionFields } from "../../../contexts/permissions/PermissionsTypes";
import I18n from "../../../core/localization/I18n";
import { useCurrentOrganisationId } from "../../../services/local/organisationContext/OrganisationContextModel";
import { Panel } from "../../../components/ui/Panel";
import { Icon, IconSymbols } from "../../../components/ui/Icon";
import { Enums } from "../../../enums";
import { ButtonTypes, LinkButton } from "../../../components/ui/Button";
import "../../../../src/apps/_shell/navigation/_settingsnav.scss";
import { Animations } from "../../../core/util/Animations";
import { ShellPages } from "../routes/shellPages";

interface SettingsNavigationProps {
  isNavExpanded: boolean;
}

export const SettingsNavigation: React.FC<SettingsNavigationProps> = props => {
  const [isTooltipContentShown, setIsTooltipContentShown] = useState(false);
  const toolTipContentRef = useRef<HTMLDivElement>(null);
  const settingContainerRef = useRef<HTMLDivElement>(null);
  const path = useLocation();
  let pathName = path.pathname;
  const organisationSettingsRegex = /organisations\/([0-9]*)\/settings(\d|\/|$)/g;
  const coreDataSettingsRegex = /organisations\/([0-9]*)\/core-data(\d|\/|$)/g;
  const programmeSettingsRegex = /organisations\/([0-9]*)\/programmes\/([0-9]*)\/settings(\d|\/|$)/g;
  const projectSettingsRegex = /organisations\/([0-9]*)\/projects\/([0-9]*)\/settings(\d|\/|$)/g;
  const organisationId = useCurrentOrganisationId();
  const [, projectId] = parseUrlForProjectId(pathName);
  const [, programmeId] = parseUrlForProgrammeId(pathName);
  const currentProgramme = useGetProgrammeByProjectId(organisationId, projectId);
  const isSettingsActive =
    organisationSettingsRegex.test(pathName) ||
    coreDataSettingsRegex.test(pathName) ||
    programmeSettingsRegex.test(pathName) ||
    projectSettingsRegex.test(pathName);

  let isOrganisation = !!organisationId;
  let isProject = !!projectId;
  const isOrganisationReader = PermissionsContext.isOrganisationReader(organisationId);
  const isOrganisationOwner = PermissionsContext.isOranisationOwnerOrAdmin(organisationId);
  const canEdiProject =
    isOrganisation && isProject
      ? PermissionsContext.canEditField(PermissionFields.PROJECTS, organisationId, projectId)
      : false;
  const canContributeToOrg = PermissionsContext.canContributeOrg(organisationId) || canEdiProject;
  useEffect(() => {
    if (!isTooltipContentShown) return;
    const clickingOutsideTooltip = e => {
      if (
        toolTipContentRef.current &&
        !toolTipContentRef.current.contains(e.target) &&
        !settingContainerRef.current.contains(e.target)
      ) {
        setIsTooltipContentShown(false);
      }
    };
    document.addEventListener("mousedown", clickingOutsideTooltip);

    return () => {
      document.removeEventListener("mousedown", clickingOutsideTooltip);
    };
  }, [isTooltipContentShown]);

  useEffect(() => {
    if (isTooltipContentShown) {
      setIsTooltipContentShown(false);
    }
    // eslint-disable-next-line
  }, [path.pathname]);

  return (
    <div className="side-nav-settings__container">
      <div className="navigation-settings__label-tooltip">
        {!isTooltipContentShown && !canContributeToOrg ? (
          <Panel.Panel
            hasBorderRadius={true}
            background={Panel.PanelBackgrounds.BG_WHITE}
            hasShadow={true}
            className={`p-2 ${Animations.FADE_IN} speed-3`}
          >
            <p className="mb-0 text-dark">{I18n.t("phrases.settingsNoAccess")}</p>
          </Panel.Panel>
        ) : (
          !props.isNavExpanded && (
            <Panel.Panel
              hasBorderRadius={true}
              background={Panel.PanelBackgrounds.BG_WHITE}
              hasShadow={true}
              className={`p-2 ${Animations.FADE_IN} speed-3`}
            >
              <p className="mb-0 text-dark">{I18n.t("phrases.settings")}</p>
            </Panel.Panel>
          )
        )}
      </div>
      <div
        className={`
            side-nav-settings__action 
            ${Animations.ZOOM_IN} 
            ${isTooltipContentShown || isSettingsActive ? "side-nav-settings__action--active" : ""}
            speed-3`}
        onClick={() => {
          if (!canContributeToOrg) return;
          setIsTooltipContentShown(prev => !prev);
        }}
        ref={settingContainerRef}
      >
        <Icon
          symbol={IconSymbols.SettingsFilled}
          size={Enums.UiSizes.MD}
          disabled={!canContributeToOrg}
          className={`side-nav-settings__icon
                ${isTooltipContentShown ? "side-nav-settings__icon--active" : ""}`}
        />
        {props.isNavExpanded ? (
          <span className={`side-nav-settings__action--text speed-2 ${Animations.SLIDE_IN_UP}`}>
            {I18n.t("phrases.settings")}
          </span>
        ) : (
          ""
        )}
      </div>

      {isTooltipContentShown && (
        <div ref={toolTipContentRef}>
          <Panel.Panel
            hasBorderRadius={true}
            hasShadow={true}
            background={Panel.PanelBackgrounds.BG_WHITE}
            className={`p-2 
              ${Animations.FADE_IN} 
              speed-3 
              side-nav-settings__tooltip-content`}
            style={{ width: "160px" }}
          >
            <LinkButton
              className="side-nav-settings_item"
              href={ShellPages.organisations.settings.index.generateLink(organisationId)}
              isDisabled={!isOrganisation || !isOrganisationOwner}
              type={ButtonTypes.LINK}
            >
              {I18n.t("phrases.organisationSettings")}
            </LinkButton>
            <LinkButton
              className="side-nav-settings_item"
              href={ShellPages.organisations.coreData.index.generateLink(organisationId)}
              isDisabled={!isOrganisation || isOrganisationReader}
              type={ButtonTypes.LINK}
            >
              {I18n.t("phrases.coreData")}
            </LinkButton>
            <LinkButton
              className="side-nav-settings_item"
              href={
                programmeId || currentProgramme
                  ? `${ShellPages.programmes.settings.index.generateLink(
                      organisationId,
                      programmeId || currentProgramme.id
                    )}`
                  : pathName
              }
              isDisabled={(!!!programmeId && !!!currentProgramme) || isOrganisationReader}
              type={ButtonTypes.LINK}
            >
              {I18n.t("phrases.programmeSettings")}
            </LinkButton>
            <LinkButton
              className="side-nav-settings_item"
              href={
                projectId ? `${ShellPages.projects.settings.index.generateLink(organisationId, projectId)}` : pathName
              }
              isDisabled={!canEdiProject}
              type={ButtonTypes.LINK}
            >
              {I18n.t("phrases.projectSettings")}
            </LinkButton>
          </Panel.Panel>
        </div>
      )}
    </div>
  );
};
